import { useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { AdminChurch, Person, Subscription } from '../../shared/api/types';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { formatPrice, INTERVAL_LABELS, STATUS_LABELS, isLapsed } from '../billing/format';
import { useMe, useSession } from '../session/SessionProvider';
import { formatDay } from './AdminChurchesPage';
import { AdminDenominationCard } from '../denomination/denomination';
import type { ChurchProfile } from '../../shared/api/types';
import { LocationPicker } from '../../shared/ui/LocationPicker';
import { countryName } from '../../shared/geo/places';

interface MemberItem {
  id: string;
  status: string;
  requested_at: string;
  started_at: string | null;
  person: Person | null;
}

const PROVIDER_NAMES: Record<string, string> = { mercadopago: 'Mercado Pago', stripe: 'Stripe', simulated: 'Simulado' };

function MembersCard({ churchId }: { churchId: string }) {
  const [status, setStatus] = useState<'active' | 'pending'>('active');
  const members = useAsync(
    (signal) => api.get<{ items: MemberItem[] }>(`/multichurch/churches/${churchId}/memberships?status=${status}`, signal).then((r) => r.items),
    [churchId, status],
  );
  return (
    <Card title="Miembros">
      <div className="actions" role="group" aria-label="Filtrar miembros">
        <button type="button" className={`chip ${status === 'active' ? 'chip--active' : ''}`} aria-pressed={status === 'active'} onClick={() => setStatus('active')}>
          Activos
        </button>
        <button type="button" className={`chip ${status === 'pending' ? 'chip--active' : ''}`} aria-pressed={status === 'pending'} onClick={() => setStatus('pending')}>
          Por aprobar
        </button>
      </div>
      {members.status === 'loading' ? <Loading /> : null}
      {members.status === 'error' ? <ErrorState error={members.error} onRetry={members.reload} /> : null}
      {members.status === 'ready' && members.data.length === 0 ? (
        <EmptyState title={status === 'active' ? 'Sin miembros activos.' : 'Nadie por aprobar.'} />
      ) : null}
      {members.status === 'ready' && members.data.length > 0 ? (
        <ul className="admin-members">
          {members.data.map((m) => (
            <li key={m.id}>
              <span>{m.person ? `${m.person.givenName} ${m.person.familyName}`.trim() : 'Persona'}</span>
              <span className="small muted">{status === 'active' ? `desde ${formatDay(m.started_at)}` : `pidió el ${formatDay(m.requested_at)}`}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </Card>
  );
}

/** Ficha de una Iglesia en el panel de la administración de KEORA. */
export function AdminChurchPage() {
  const me = useMe();
  const { switchContext } = useSession();
  const navigate = useNavigate();
  const { churchId = '' } = useParams();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const churches = useAsync((signal) => api.get<{ items: AdminChurch[] }>('/multichurch/admin/churches', signal).then((r) => r.items), []);
  if (!me.isPlatformAdmin) return <Navigate to="/app" replace />;

  const church = churches.status === 'ready' ? churches.data.find((c) => c.id === churchId) : undefined;

  async function enterSupport() {
    setBusy(true);
    setError(null);
    try {
      await switchContext(churchId);
      navigate('/app');
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  }

  async function toggleStatus() {
    if (!church) return;
    const suspending = church.status === 'active';
    const reason = window.prompt(
      suspending
        ? `¿Por qué suspendes "${church.name}"? Nadie de la iglesia podrá usarla hasta que la reactives; sus datos se conservan.`
        : `¿Por qué reactivas "${church.name}"?`,
    );
    if (reason === null) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/multichurch/admin/churches/${churchId}/${suspending ? 'suspend' : 'reactivate'}`, { reason });
      churches.reload();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page">
      <p>
        <Link to="/app/admin/iglesias">← Todas las iglesias</Link>
      </p>
      {churches.status === 'loading' ? <Loading /> : null}
      {churches.status === 'error' ? <ErrorState error={churches.error} onRetry={churches.reload} /> : null}
      {churches.status === 'ready' && !church ? <EmptyState title="No encontré esa iglesia." /> : null}
      {church ? (
        <>
          <header className="page__header">
            <h1>{church.name}</h1>
            <p className="muted">
              mykeora.com/i/{church.slug} · registrada el {formatDay(church.createdAt)} · zona horaria {church.timezone}
            </p>
          </header>
          {error ? <Alert>{error}</Alert> : null}
          {church.status === 'suspended' ? <Alert>Esta iglesia está suspendida: sus miembros no pueden usarla.</Alert> : null}

          <Card title="Resumen">
            <dl className="details">
              <dt>Registró</dt>
              <dd>
                {church.founder ? (
                  <>
                    {church.founder.name}
                    {church.founder.email ? (
                      <>
                        {' · '}
                        <a href={`mailto:${church.founder.email}`}>{church.founder.email}</a>
                      </>
                    ) : null}
                  </>
                ) : (
                  '—'
                )}
              </dd>
              <dt>Miembros</dt>
              <dd>
                {church.members.active} activos · {church.members.pending} por aprobar
              </dd>
              <dt>Plan</dt>
              <dd>{church.subscription ? church.subscription.planName : 'Sin plan'}</dd>
              {church.subscription ? (
                <>
                  <dt>Suscripción</dt>
                  <dd>
                    <span className={`badge ${isLapsed(church.subscription.status) ? 'badge--warn' : 'badge--active'}`}>
                      {STATUS_LABELS[church.subscription.status]}
                    </span>{' '}
                    {church.subscription.endsAt
                      ? `${church.subscription.status === 'trialing' || church.subscription.status === 'trial_expired' ? 'prueba hasta' : 'pagado hasta'} ${formatDay(church.subscription.endsAt)}`
                      : ''}
                  </dd>
                  <dt>Último pago con</dt>
                  <dd>{church.subscription.provider ? (PROVIDER_NAMES[church.subscription.provider] ?? church.subscription.provider) : '—'}</dd>
                </>
              ) : null}
            </dl>
            <div className="actions">
              <Button onClick={enterSupport} busy={busy}>
                Entrar a esta iglesia (modo soporte)
              </Button>
              <Button variant="ghost" onClick={toggleStatus} busy={busy}>
                {church.status === 'active' ? 'Suspender' : 'Reactivar'}
              </Button>
            </div>
            <p className="small muted">
              En modo soporte ves y usas la iglesia como su administrador. Cada acción queda en la auditoría. Arriba verás una franja de aviso;
              con «Salir» regresas al Panel KEORA.
            </p>
          </Card>

          <ProfileCard churchId={church.id} slug={church.slug} />

          <AdminDenominationCard churchId={church.id} />

          {church.subscription ? <TermsCard churchId={church.id} /> : null}

          <MembersCard churchId={church.id} />
        </>
      ) : null}
    </div>
  );
}

type AdminSub = Subscription & { note: string | null };

/** Condiciones comerciales de la iglesia (solo Superadmin): plan, descuento, cortesía y extras. */
function TermsCard({ churchId }: { churchId: string }) {
  const data = useAsync(
    async (signal) => {
      const [sub, plans] = await Promise.all([
        api.get<{ subscription: AdminSub | null }>(`/saas/admin/churches/${churchId}/subscription`, signal).then((r) => r.subscription),
        api.get<{ items: { code: string; name: string; archived: boolean }[] }>('/saas/admin/plans', signal).then((r) => r.items.filter((p) => !p.archived)),
      ]);
      return { sub, plans };
    },
    [churchId],
  );
  const [msg, setMsg] = useState<{ tone: 'error' | 'success'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  if (data.status !== 'ready' || !data.data.sub) return null;
  const { sub: s, plans } = data.data;
  const base = `/saas/admin/churches/${churchId}/subscription`;
  const run = async (action: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    setMsg(null);
    try {
      await action();
      setMsg({ tone: 'success', text: ok });
      data.reload();
    } catch (e) {
      setMsg({ tone: 'error', text: errorMessage(e) });
    } finally {
      setBusy(false);
    }
  };
  const dateOnly = (d: string | null | undefined) => (d ? String(d).slice(0, 10) : '');
  return (
    <Card title="Condiciones comerciales">
      {msg ? <Alert tone={msg.tone}>{msg.text}</Alert> : null}
      {s.status === 'pending' ? (
        <form
          className="family-add"
          onSubmit={(e) => {
            e.preventDefault();
            const trialDays = Number(new FormData(e.currentTarget).get('trialDays'));
            void run(() => api.post(`${base}/activate`, { trialDays }), 'Iglesia activada. Su prueba empieza hoy.');
          }}
        >
          <p className="small">Esta iglesia espera activación: se activa sola al pagar, o aquí a mano.</p>
          <Field label="Días de prueba" name="trialDays" type="number" min={1} max={365} defaultValue={s.plan.trialDays || 15} required />
          <Button type="submit" busy={busy}>Activar iglesia</Button>
        </form>
      ) : null}
      <p className="small">
        Paga {s.price ? formatPrice(s.price.totalCents, s.plan.currency) : '—'} {s.interval ? `(${INTERVAL_LABELS[s.interval]?.toLowerCase()})` : ''} · Miembros {s.members?.count ?? 0}
        {s.members?.limit != null ? ` de ${s.members.limit}` : ' (sin límite)'}
      </p>
      <form className="family-add" onSubmit={(e) => { e.preventDefault(); const planCode = String(new FormData(e.currentTarget).get('planCode')); void run(() => api.put(`${base}/plan`, { planCode }), 'Plan cambiado.'); }}>
        <div className="field">
          <label htmlFor="terms-plan">Plan</label>
          <select id="terms-plan" name="planCode" defaultValue={s.plan.code}>
            {plans.map((p) => <option key={p.code} value={p.code}>{p.name}</option>)}
          </select>
        </div>
        <Button type="submit" variant="secondary" busy={busy}>Cambiar plan</Button>
      </form>
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          const f = new FormData(e.currentTarget);
          const t = (k: string) => String(f.get(k) ?? '').trim() || null;
          void run(() => api.put(`${base}/terms`, { discountPercent: Number(t('discountPercent') ?? 0), discountUntil: t('discountUntil'), courtesyUntil: f.get('permanent') === 'on' ? '9999-12-31' : t('courtesyUntil'), note: t('note') }), 'Condiciones guardadas.');
        }}
      >
        <div className="form__row form__row--3">
          <Field label="Descuento (%)" name="discountPercent" type="number" min={0} max={100} defaultValue={s.discount?.percent ?? 0} />
          <Field label="Descuento hasta (opcional)" name="discountUntil" type="date" defaultValue={dateOnly(s.discount?.until)} />
          <Field label="Cortesía hasta" name="courtesyUntil" type="date" defaultValue={dateOnly(s.courtesyUntil) === '9999-12-31' ? '' : dateOnly(s.courtesyUntil)} />
        </div>
        <label className="check"><input type="checkbox" name="permanent" defaultChecked={dateOnly(s.courtesyUntil) === '9999-12-31'} /> Cortesía permanente (plataforma gratis)</label>
        <Field label="Nota interna" name="note" maxLength={500} defaultValue={s.note ?? ''} />
        <Button type="submit" busy={busy}>Guardar condiciones</Button>
      </form>
      {(s.modules ?? []).length > 0 ? <h3 className="small">Módulos extra</h3> : null}
      {(s.modules ?? []).map((m) =>
        m.contracted ? (
          <div key={m.code} className="list__item">
            <span>
              <strong>{m.name}</strong> · activo · este mes {m.used} de {m.monthlyLimit}
            </span>
            <Button variant="ghost" busy={busy} onClick={() => void run(() => api.delete(`${base}/modules/${m.code}`), `${m.name}: desactivado.`)}>
              Quitar
            </Button>
          </div>
        ) : (
          <form
            key={m.code}
            className="family-add"
            onSubmit={(e) => {
              e.preventDefault();
              const price = String(new FormData(e.currentTarget).get('price') ?? '').trim();
              void run(() => api.post(`${base}/modules/${m.code}`, { monthlyPriceCents: price === '' ? null : Math.round(Number(price) * 100) }), `${m.name}: activado para esta iglesia.`);
            }}
          >
            <Field
              label={`${m.name}: precio al mes para esta iglesia`}
              name="price"
              type="number"
              min={0}
              step="0.01"
              defaultValue={m.monthlyPriceCents === null ? '' : m.monthlyPriceCents / 100}
              hint="0 = sin costo. Vacío = el precio general del módulo."
            />
            <Button type="submit" variant="secondary" busy={busy}>
              Activar {m.name}
            </Button>
          </form>
        ),
      )}
      <h3 className="small">Extras</h3>
      <ul className="list">
        {(s.addons ?? []).filter((a) => !a.moduleCode).map((a) => (
          <li key={a.id} className="list__item">
            <span>{a.description}{a.quantity > 1 ? ` ×${a.quantity}` : ''} · {formatPrice(a.monthlyPriceCents, s.plan.currency)}/mes{a.extraMembers ? ` · +${a.extraMembers} miembros` : ''}</span>
            <Button variant="ghost" busy={busy} onClick={() => void run(() => api.delete(`${base}/addons/${a.id}`), 'Extra quitado.')}>Quitar</Button>
          </li>
        ))}
      </ul>
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          const f = new FormData(e.currentTarget);
          void run(() => api.post(`${base}/addons`, { description: String(f.get('description') ?? '').trim(), monthlyPriceCents: Math.round(Number(f.get('price') ?? 0) * 100), quantity: Number(f.get('quantity') ?? 1), extraMembers: Number(f.get('extraMembers') ?? 0) }), 'Extra agregado.').then(() => (e.target as HTMLFormElement).reset());
        }}
      >
        <div className="form__row">
          <Field label="Extra" name="description" required maxLength={120} placeholder="100 miembros adicionales" />
          <Field label="Precio al mes" name="price" type="number" min={0} step="0.01" required />
        </div>
        <div className="form__row">
          <Field label="Cantidad" name="quantity" type="number" min={1} defaultValue={1} />
          <Field label="Miembros que suma (si aplica)" name="extraMembers" type="number" min={0} defaultValue={0} />
        </div>
        <Button type="submit" variant="secondary" busy={busy}>Agregar extra</Button>
      </form>
    </Card>
  );
}

/** Lo que capturó la iglesia (dirección, mapa, contacto): se revisa antes de activarla. */
function ProfileCard({ churchId, slug }: { churchId: string; slug: string }) {
  const data = useAsync((signal) => api.get<ChurchProfile & { logoUrl: string | null }>(`/multichurch/admin/churches/${churchId}/profile`, signal), [churchId]);
  if (data.status === 'loading') return <Card title="Datos y ubicación"><Loading /></Card>;
  if (data.status === 'error') return <Card title="Datos y ubicación"><ErrorState error={data.error} onRetry={data.reload} /></Card>;
  const p = data.data;
  const spot = p.latitude != null && p.longitude != null ? { lat: p.latitude, lng: p.longitude } : null;
  const place = [p.city, p.state, p.country ? countryName(p.country) : null].filter(Boolean).join(', ');
  return (
    <Card title="Datos y ubicación">
      {p.logoUrl ? <img src={p.logoUrl} alt="Logo de la iglesia" className="admin-church-logo" /> : null}
      <dl className="details">
        <dt>Dirección</dt>
        <dd>{p.address || <span className="muted">Sin dirección</span>}</dd>
        <dt>Ciudad</dt>
        <dd>{place || <span className="muted">—</span>}</dd>
        {p.pastorName ? (<><dt>Pastor</dt><dd>{p.pastorName}</dd></>) : null}
        {p.phone ? (<><dt>Teléfono</dt><dd>{p.phone}</dd></>) : null}
        {p.email ? (<><dt>Correo</dt><dd>{p.email}</dd></>) : null}
        {p.denomination ? (<><dt>Denominación (texto)</dt><dd>{p.denomination}</dd></>) : null}
        {p.website ? (<><dt>Sitio web</dt><dd><a href={p.website} target="_blank" rel="noopener noreferrer">{p.website}</a></dd></>) : null}
      </dl>
      <LocationPicker readOnly value={spot} label="Ubicación de la iglesia" />
      <div className="actions actions--wrap">
        {spot ? (
          <a className="btn btn--secondary" href={p.mapsUrl ?? `https://www.google.com/maps?q=${spot.lat},${spot.lng}`} target="_blank" rel="noopener noreferrer">
            Ver en Google Maps
          </a>
        ) : null}
        <a className="btn btn--ghost" href={`/i/${slug}`} target="_blank" rel="noopener noreferrer">
          Ver su página pública
        </a>
      </div>
    </Card>
  );
}
